<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click="$emit('close')"
    >
      <div
        class="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto"
        @click.stop
      >
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-xl font-bold">Quality Campaign</h2>
          <button class="text-gray-400 hover:text-gray-600" @click="$emit('close')">
            <FontAwesomeIcon :icon="['fas', 'xmark']" />
          </button>
        </div>

        <div class="flex gap-2 mb-4">
          <button :class="tabClass('select')" @click="mode = 'select'">Select existing</button>
          <button :class="tabClass('create')" @click="startCreate">Create new</button>
        </div>

        <div v-if="mode === 'select'">
          <div v-if="loading" class="text-sm text-gray-500">Loading…</div>
          <div v-else-if="campaigns.length === 0" class="text-sm text-gray-500">
            No campaigns yet. Create one.
          </div>
          <ul v-else class="divide-y border rounded">
            <li
              v-for="c in campaigns"
              :key="c.id"
              class="p-3 hover:bg-gray-50 cursor-pointer flex items-center justify-between gap-3"
              @click="choose(c)"
            >
              <div class="min-w-0">
                <div class="font-medium sensitive" data-demo-blur>{{ c.name }}</div>
                <div class="text-xs text-gray-500 mb-1.5">{{ c.start_date }} → {{ c.end_date }}</div>
                <div class="flex flex-wrap gap-1 mb-1">
                  <template v-if="(c.scope_entities_detail || []).length">
                    <span
                      v-for="e in chips(c.scope_entities_detail, 3).shown"
                      :key="e.id"
                      class="px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 sensitive"
                      data-demo-blur
                      :title="e.name"
                    >{{ e.name }}</span>
                    <span
                      v-if="chips(c.scope_entities_detail, 3).extra"
                      class="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
                      :title="extraTitle(c.scope_entities_detail, 3, 'name')"
                    >+{{ chips(c.scope_entities_detail, 3).extra }}</span>
                  </template>
                  <span v-else class="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">All contracts</span>
                </div>
                <div class="flex flex-wrap gap-1">
                  <template v-if="(c.metrics_detail || []).length">
                    <span
                      v-for="m in chips(c.metrics_detail, 6).shown"
                      :key="m.id"
                      class="px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700"
                      :title="m.name"
                    >{{ m.measure_code }}</span>
                    <span
                      v-if="chips(c.metrics_detail, 6).extra"
                      class="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
                      :title="extraTitle(c.metrics_detail, 6, 'measure_code')"
                    >+{{ chips(c.metrics_detail, 6).extra }}</span>
                  </template>
                  <span v-else class="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">All measures</span>
                </div>
                <div v-if="genResult[c.id]" class="mt-1.5 text-xs text-gray-600">{{ genResultText(c.id) }}</div>
              </div>
              <div class="flex items-center gap-2 shrink-0">
                <span
                  v-if="c.is_active"
                  class="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800"
                >Active</span>
                <button
                  class="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  :disabled="genBusyId === c.id"
                  title="Preview how many opportunities would be generated"
                  @click.stop="runGenerate(c, true)"
                >Dry run</button>
                <button
                  class="text-xs px-2 py-1 rounded bg-blue-dark text-white hover:opacity-90 disabled:opacity-50"
                  :disabled="genBusyId === c.id"
                  title="Generate / refresh this campaign's opportunities"
                  @click.stop="runGenerate(c, false)"
                >{{ genBusyId === c.id ? '…' : 'Generate' }}</button>
                <button
                  class="text-gray-400 hover:text-blue-dark"
                  title="Edit campaign"
                  @click.stop="startEdit(c)"
                >
                  <FontAwesomeIcon :icon="['fas', 'pen']" />
                </button>
              </div>
            </li>
          </ul>
        </div>

        <div v-else>
          <div class="text-sm font-semibold text-gray-700 mb-3">
            {{ editingId ? 'Edit campaign' : 'New campaign' }}
          </div>
          <div class="grid grid-cols-2 gap-3 mb-3">
            <div class="col-span-2">
              <label class="block text-sm font-medium mb-1">Name</label>
              <input
                v-model="form.name"
                type="text"
                class="w-full border rounded px-3 py-2"
                placeholder="e.g. Anthem Medicaid Sprint"
              />
            </div>
            <div>
              <label class="block text-sm font-medium mb-1">Start date</label>
              <input v-model="form.start_date" type="date" class="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label class="block text-sm font-medium mb-1">End date</label>
              <input v-model="form.end_date" type="date" class="w-full border rounded px-3 py-2" />
            </div>
          </div>

          <div class="mb-3">
            <label class="block text-sm font-medium mb-1">Contracts (payer × LOB)</label>
            <SimpleMultiSelect
              v-model="form.contract_ids"
              :options="contractOptions"
              :searchable="true"
              :sensitive-labels="true"
              placeholder="Select contracts…"
            />
            <p class="text-xs text-gray-500 mt-1">Leave empty = whole book.</p>
          </div>

          <div class="mb-3">
            <label class="block text-sm font-medium mb-1">Measures (gap codes)</label>
            <SimpleMultiSelect
              v-model="form.measure_ids"
              :options="measureOptions"
              :sensitive-labels="false"
              :searchable="true"
              placeholder="Select measures…"
            />
            <p class="text-xs text-gray-500 mt-1">Leave empty = all measures.</p>
          </div>

          <div class="mb-4">
            <label class="block text-sm font-medium mb-1">Assigned analysts</label>
            <SimpleMultiSelect
              v-model="form.analyst_ids"
              :options="analystOptions"
              :sensitive-labels="true"
              :searchable="true"
              placeholder="Select analysts…"
            />
            <p class="text-xs text-gray-500 mt-1">Leave empty = visible to managers only.</p>
          </div>

          <div class="flex items-center gap-2 mb-4">
            <input id="qc-active" v-model="form.is_active" type="checkbox" />
            <label for="qc-active" class="text-sm">Active</label>
          </div>

          <div class="flex gap-2">
            <button
              class="btn-primary flex-1"
              :disabled="!canSave || saving"
              @click="save"
            >
              {{ saving ? 'Saving…' : (editingId ? 'Save & apply' : 'Create & apply') }}
            </button>
            <button class="btn-secondary flex-1" :disabled="saving" @click="$emit('close')">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';

export default {
  name: 'Quality_Campaign_Modal',
  components: { FontAwesomeIcon, SimpleMultiSelect },
  props: {
    isOpen: { type: Boolean, default: false },
  },
  emits: ['close', 'select', 'generated'],
  data() {
    return {
      mode: 'select',
      editingId: null,
      loading: false,
      saving: false,
      campaigns: [],
      genBusyId: null,
      genResult: {},
      contracts: [],
      measures: [],
      analysts: [],
      form: {
        name: '',
        start_date: '',
        end_date: '',
        contract_ids: [],
        measure_ids: [],
        analyst_ids: [],
        is_active: true,
      },
    };
  },
  computed: {
    contractOptions() {
      return this.contracts.map((c) => ({ value: c.id, label: c.name }));
    },
    measureOptions() {
      return this.measures.map((m) => ({
        value: m.id,
        label: `${m.measure_code} — ${m.name}`,
      }));
    },
    analystOptions() {
      return this.analysts.map((a) => ({ value: a.id, label: a.name }));
    },
    canSave() {
      return Boolean(this.form.name.trim() && this.form.start_date && this.form.end_date);
    },
  },
  watch: {
    isOpen(open) {
      if (open) {
        this.mode = 'select';
        this.editingId = null;
        this.resetForm();
        this.loadAll();
      }
    },
  },
  methods: {
    tabClass(m) {
      return [
        'px-3 py-1.5 rounded text-sm',
        this.mode === m ? 'bg-blue-dark text-white' : 'bg-gray-100 text-gray-700',
      ];
    },
    resetForm() {
      this.form = {
        name: '',
        start_date: '',
        end_date: '',
        contract_ids: [],
        measure_ids: [],
        analyst_ids: [],
        is_active: true,
      };
    },
    async loadAll() {
      this.loading = true;
      try {
        const [list, opts] = await Promise.all([
          this.$axios.get('/api/opportunity-campaigns/'),
          this.$axios.get('/api/opportunity-campaigns/options/'),
        ]);
        this.campaigns = list.data?.results || list.data || [];
        this.contracts = opts.data?.contracts || [];
        this.measures = opts.data?.measures || [];
        this.analysts = opts.data?.analysts || [];
      } catch (e) {
        console.error('Failed to load quality campaigns', e);
      } finally {
        this.loading = false;
      }
    },
    async runGenerate(c, dryRun) {
      this.genBusyId = c.id;
      try {
        const res = await this.$axios.post(
          `/api/opportunity-campaigns/${c.id}/generate/`,
          { dry_run: dryRun },
        );
        this.genResult = { ...this.genResult, [c.id]: { ...res.data, dry_run: dryRun } };
        if (!dryRun) {
          await this.loadAll();
          this.$emit('generated', c);
        }
      } catch (e) {
        console.error('Campaign generation failed', e);
      } finally {
        this.genBusyId = null;
      }
    },
    genResultText(id) {
      const r = this.genResult[id];
      if (!r) return '';
      if (r.dry_run) {
        return `Dry run: ${r.to_create} to create · cohort ${r.cohort_size} · ${r.already_generated} existing`;
      }
      return `Generated ${r.created} · cohort ${r.cohort_size} · ${r.already_generated} already existed`;
    },
    chips(items, max) {
      const list = items || [];
      return { shown: list.slice(0, max), extra: Math.max(0, list.length - max) };
    },
    extraTitle(items, max, key) {
      return (items || []).slice(max).map((i) => i[key]).join(', ');
    },
    choose(campaign) {
      this.$emit('select', campaign);
    },
    startCreate() {
      this.editingId = null;
      this.resetForm();
      this.mode = 'create';
    },
    startEdit(campaign) {
      this.editingId = campaign.id;
      this.form = {
        name: campaign.name || '',
        start_date: campaign.start_date || '',
        end_date: campaign.end_date || '',
        contract_ids: (campaign.scope_entities_detail || []).map((e) => e.id),
        measure_ids: (campaign.metrics_detail || []).map((m) => m.id),
        analyst_ids: (campaign.assigned_members_detail || []).map((e) => e.id),
        is_active: !!campaign.is_active,
      };
      this.mode = 'create';
    },
    async save() {
      if (!this.canSave) return;
      this.saving = true;
      const payload = {
        name: this.form.name.trim(),
        start_date: this.form.start_date,
        end_date: this.form.end_date,
        scope_entities: this.form.contract_ids,
        metrics: this.form.measure_ids,
        assigned_members: this.form.analyst_ids,
        is_active: this.form.is_active,
      };
      try {
        const { data } = this.editingId
          ? await this.$axios.patch(`/api/opportunity-campaigns/${this.editingId}/`, payload)
          : await this.$axios.post('/api/opportunity-campaigns/', payload);
        this.$emit('select', data);
      } catch (e) {
        console.error('Failed to save quality campaign', e);
        const detail = e.response?.data ? JSON.stringify(e.response.data) : 'Failed to save campaign';
        alert(detail);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
